'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  content: string;
  image: string;
  alt: string;
  rating: number;
}

interface TestimonialsSectionProps {
  className?: string;
}

const TestimonialsSection = ({ className = '' }: TestimonialsSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Dr. Michael Chen',
    role: 'Chief Engineer',
    company: 'Orbital Dynamics Inc.',
    content: 'Techaero\'s precision and attention to detail transformed our satellite component manufacturing. Their AS9100 processes and technical expertise gave us complete confidence in mission-critical parts. The digital platform made project tracking seamless.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d805f55-1763295574780.png",
    alt: 'Asian man in glasses and professional attire in aerospace engineering office',
    rating: 5
  },
  {
    id: 2,
    name: 'Colonel Sarah Martinez',
    role: 'Program Manager',
    company: 'Defense Systems Corp.',
    content: 'Working with Techaero on classified defense programs has been exceptional. Their ITAR compliance, security protocols, and manufacturing excellence meet the highest defense standards. True mission-critical partnership.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13bdacd03-1763298932646.png",
    alt: 'Professional woman with short brown hair in navy blazer in defense facility',
    rating: 5
  },
  {
    id: 3,
    name: 'James Thompson',
    role: 'VP of Engineering',
    company: 'NewSpace Ventures',
    content: 'As a startup, we needed a manufacturing partner who understood both quality and speed. Techaero delivered aerospace-grade precision without traditional industry barriers. Their team became an extension of ours.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1105936ba-1772472652567.png",
    alt: 'Man with gray hair in business casual attire in modern startup office',
    rating: 5
  },
  {
    id: 4,
    name: 'Dr. Priya Patel',
    role: 'Quality Director',
    company: 'Aerospace Innovations Ltd.',
    content: 'The quality documentation and testing capabilities at Techaero exceed industry standards. Their NADCAP accreditation and comprehensive quality systems give us complete traceability and confidence in every component.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19ed678c3-1772749024920.png",
    alt: 'Indian woman in white lab coat with safety glasses in quality control laboratory',
    rating: 5
  }];


  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 lg:py-28 bg-card ${className}`}>
        <div className="mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
                <span className="font-cta text-xs text-accent tracking-wider">CLIENT TESTIMONIALS</span>
              </div>
              <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
                Trusted by Aerospace Leaders
              </h2>
            </div>

            <div className="bg-background border border-border rounded-xl p-8 lg:p-12">
              <div className="flex items-start space-x-6 mb-6">
                <div className="relative w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={testimonials[0].image}
                    alt={testimonials[0].alt}
                    className="w-full h-full object-cover" />

                </div>
                <div className="flex-1">
                  <h3 className="font-headline text-xl font-bold text-foreground mb-1">
                    {testimonials[0].name}
                  </h3>
                  <p className="font-body text-sm text-technical-gray mb-1">
                    {testimonials[0].role}
                  </p>
                  <p className="font-cta text-xs text-accent">{testimonials[0].company}</p>
                </div>
              </div>
              <p className="font-body text-lg text-foreground leading-relaxed">
                "{testimonials[0].content}"
              </p>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className={`py-20 lg:py-28 bg-card ${className}`}>
      <div className="mx-auto px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
              <span className="font-cta text-xs text-accent tracking-wider">CLIENT TESTIMONIALS</span>
            </div>
            <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Trusted by Aerospace Leaders
            </h2>
            <p className="font-body text-lg text-technical-gray max-w-3xl mx-auto">
              Hear from aerospace engineers, program managers, and quality professionals who trust Techaero for mission-critical manufacturing excellence.
            </p>
          </div>

          <div className="relative">
            <div className="bg-background border border-accent/30 rounded-xl p-8 lg:p-12 shadow-glow">
              <div className="flex items-start space-x-6 mb-6">
                <div className="relative w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border-2 border-accent">
                  <AppImage
                    src={testimonials[activeIndex].image}
                    alt={testimonials[activeIndex].alt}
                    className="w-full h-full object-cover" />

                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(testimonials[activeIndex].rating)].map((_, i) =>
                    <Icon key={i} name="StarIcon" size={16} className="text-mission-orange" variant="solid" />
                    )}
                  </div>
                  <h3 className="font-headline text-xl font-bold text-foreground mb-1">
                    {testimonials[activeIndex].name}
                  </h3>
                  <p className="font-body text-sm text-technical-gray mb-1">
                    {testimonials[activeIndex].role}
                  </p>
                  <p className="font-cta text-xs text-accent">{testimonials[activeIndex].company}</p>
                </div>
              </div>

              <div className="mb-8">
                <Icon name="ChatBubbleLeftIcon" size={32} className="text-accent/30 mb-4" />
                <p className="font-body text-lg text-foreground leading-relaxed">
                  "{testimonials[activeIndex].content}"
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  {testimonials.map((_, index) =>
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeIndex ? 'bg-accent w-8' : 'bg-border hover:bg-accent/50'}`
                    }
                    aria-label={`Go to testimonial ${index + 1}`} />

                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={prevTestimonial}
                    className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-300"
                    aria-label="Previous testimonial">

                    <Icon name="ChevronLeftIcon" size={20} />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-300"
                    aria-label="Next testimonial">

                    <Icon name="ChevronRightIcon" size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) =>
            <button
              key={testimonial.id}
              onClick={() => setActiveIndex(index)}
              className={`relative rounded-lg overflow-hidden border-2 transition-all duration-300 ${
              index === activeIndex ? 'border-accent shadow-glow' : 'border-border hover:border-accent/50'}`
              }>

                <div className="aspect-square">
                  <AppImage
                  src={testimonial.image}
                  alt={testimonial.alt}
                  className="w-full h-full object-cover" />

                </div>
                {index === activeIndex &&
              <div className="absolute inset-0 bg-accent/20 flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={32} className="text-accent" variant="solid" />
                  </div>
              }
              </button>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;